import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    ArrowsClockwise,
    CaretDown,
    DotsThreeVertical,
    DownloadSimple,
    ImageSquare,
    UploadSimple,
} from '@phosphor-icons/react'
import Header from '../../components/Header/Header'
import './AddOutletPage.scss'
import merchantApi from '../../services/merchantApi'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs'
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField'
import parsePhoneNumberFromString from 'libphonenumber-js'
import { Icon } from '@iconify/react/dist/iconify.js'
import AppMenu from '../../components/AppMenu/AppMenu'
import AppPagination from '../../components/AppPagination/AppPagination'

import JSZip from "jszip";
import { saveAs } from "file-saver";

const moduleCode = 'setting'

const FORM_TABS = [
    { key: 'outlet-info', label: 'Outlet info' },
    { key: 'service-hours', label: 'Service hours' },
    { key: 'tables-qr', label: 'Tables & QR' },
    { key: 'branding', label: 'Branding' },
    { key: 'kiosks', label: 'Kiosks' },
]

const toDisplayTime = (value) => {
    if (!value) return ''
    const [rawHour, rawMinute] = String(value).split(':')
    const hourNumber = Number(rawHour)
    if (Number.isNaN(hourNumber)) return value
    const suffix = hourNumber >= 12 ? 'pm' : 'am'
    const hour12 = hourNumber % 12 || 12
    return `${hour12}:${rawMinute || '00'} ${suffix}`
}

const toApiTime = (value) => {
    if (!value || value === '-') return null

    const normalized = String(value).trim().toLowerCase()

    const shortTimeMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/)
    if (shortTimeMatch) {
        const hourNumber = Number(shortTimeMatch[1]) % 12 + (shortTimeMatch[3] === 'pm' ? 12 : 0)
        const minute = shortTimeMatch[2]
        return `${String(hourNumber).padStart(2, '0')}:${minute}`
    }

    const longTimeMatch = normalized.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (longTimeMatch) {
        return `${longTimeMatch[1].padStart(2, '0')}:${longTimeMatch[2]}`
    }

    return value
}

const toPickerValue = (value) => {
    if (!value) return null

    if (dayjs.isDayjs(value)) {
        return value
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()

        const shortTimeMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/)
        if (shortTimeMatch) {
            const hour = Number(shortTimeMatch[1]) % 12 + (shortTimeMatch[3] === 'pm' ? 12 : 0)
            const minute = Number(shortTimeMatch[2])
            return dayjs().hour(hour).minute(minute).second(0).millisecond(0)
        }
    }

    const parsed = dayjs(value)
    return parsed.isValid() ? parsed : null
}

function FormGroup({ label, optional, error, children }) {
    return (
        <div className="add-outlet-field">
            {label && (
                <label>
                    {label}
                    {optional && <span> (optional)</span>}
                </label>
            )}
            {children}
            {error && <span className="add-outlet-field__error">{error}</span>}
        </div>
    )
}

function OutletInfoTab({ outletInfo, setOutletInfo, errors }) {
    console.log("outletInfo", outletInfo)
    return (
        <section className="add-outlet-card">
            <header className="add-outlet-card__header">Outlet information</header>

            <div className="add-outlet-card__body add-outlet-form-grid">
                <div className="add-outlet-field--full">
                    <FormGroup label="Outlet display name" error={errors.name}>
                        <input
                            id="outlet-name"
                            type="text"
                            placeholder="KLCC Branch"
                            className={errors.name ? 'add-outlet-input--error' : ''}
                            value={outletInfo.name || ''}
                            onChange={(e) => setOutletInfo('name', e.target.value)}
                        />
                    </FormGroup>
                </div>

                <div className="add-outlet-field--full">
                    <FormGroup label="Address" error={errors.address}>
                        <textarea
                            id="outlet-address"
                            rows={4}
                            placeholder="Level 4, Suria KLCC, KL"
                            className={errors.address ? 'add-outlet-input--error' : ''}
                            value={outletInfo.address || ''}
                            onChange={(e) => setOutletInfo('address', e.target.value)}
                        />
                    </FormGroup>
                </div>

                <div className="add-outlet-field--full">
                    <FormGroup label="Google maps URL" error={errors.mapsUrl}>
                        <input
                            id="maps-url"
                            type="text"
                            placeholder="https://maps.google.com/?q=Hikari-House+KLCC"
                            className={errors.mapsUrl ? 'add-outlet-input--error' : ''}
                            value={outletInfo.mapsUrl || ''}
                            onChange={(e) => setOutletInfo('mapsUrl', e.target.value)}
                        />
                    </FormGroup>
                </div>

                <FormGroup label="Longitude" error={errors.longitude}>
                    <input
                        id="longitude"
                        type="text"
                        placeholder="Longitude"
                        className={errors.longitude ? 'add-outlet-input--error' : ''}
                        value={outletInfo.longitude || ''}
                        onChange={(e) => setOutletInfo('longitude', e.target.value)}
                    />
                </FormGroup>

                <FormGroup label="Latitude" error={errors.latitude}>
                    <input
                        id="latitude"
                        type="text"
                        placeholder="Latitude"
                        className={errors.latitude ? 'add-outlet-input--error' : ''}
                        value={outletInfo.latitude || ''}
                        onChange={(e) => setOutletInfo('latitude', e.target.value)}
                    />
                </FormGroup>

                {/* <FormGroup label="Phone number" error={errors.phoneNumber}> */}
                <div className="sr-panel__field">
                    <label>Phone number</label>
                    <PhoneInputField
                        value={outletInfo?.phoneNumber}
                        onChange={(value) => setOutletInfo('phoneNumber', value)}
                        countryCode={outletInfo?.countryCode || '60'}
                        onCountryCodeChange={(value) => setOutletInfo('countryCode', value)}
                        className="add-outlet-phone-field"
                    />
                </div>
                {/* </FormGroup> */}

                <FormGroup label="Email address" optional error={errors.email}>
                    <input
                        id="email-address"
                        type="email"
                        placeholder="Email address"
                        className={errors.email ? 'add-outlet-input--error' : ''}
                        value={outletInfo.email || ''}
                        onChange={(e) => setOutletInfo('email', e.target.value)}
                    />
                </FormGroup>

                <div className="add-outlet-field--full">
                    <FormGroup label="Cart inactivity timeout (minutes)" error={errors.cart_inactivity_timeout_minutes}>
                        <input
                            id="cart-inactivity-timeout"
                            type="text"
                            placeholder="Cart inactivity timeout"
                            className={errors.cart_inactivity_timeout_minutes ? 'add-outlet-input--error' : ''}
                            value={outletInfo.cartInactivityTimeout}
                            onChange={(e) => setOutletInfo('cartInactivityTimeout', e.target.value)}
                        />
                    </FormGroup>
                </div>
            </div>
        </section>
    )
}

function ServiceHoursTab({ serviceHours, setServiceHours }) {
    const businessHours = serviceHours?.businessHours?.length ? serviceHours.businessHours : []
    const defaultStartTime = serviceHours?.orderingHours?.defaultStartTime || ''
    const defaultEndTime = serviceHours?.orderingHours?.defaultEndTime || ''
    const useCustomHours = Boolean(serviceHours?.orderingHours?.useCustomHours)

    return (
        <section className="add-outlet-card">
            <header className="add-outlet-card__header">Service hours</header>

            <div className="add-outlet-card__body">
                <div className="add-outlet-hours-block">
                    <h3>Business hours</h3>
                    <div className="add-outlet-week-list">
                        {businessHours.length ? businessHours.map((item) => (
                            <div key={item.day} className="add-outlet-week-row">
                                <span>{item.day}</span>
                                <span>{item.time}</span>
                            </div>
                        )) : "No business hours available"}
                    </div>
                </div>

                <div className="add-outlet-hours-block">
                    <h3>Ordering hours</h3>

                    <div className="add-outlet-form-grid">
                        <div className="add-outlet-time-picker-container">
                            <label htmlFor="default-start">Default start time</label>
                            <div >
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <TimePicker
                                        className='add-outlet-time-picker'
                                        value={toPickerValue(defaultStartTime)}
                                        onChange={(value) => setServiceHours((prev) => ({
                                            ...prev,
                                            orderingHours: {
                                                ...prev.orderingHours,
                                                defaultStartTime: value?.format('HH:mm') || '',
                                            },
                                        }))}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>
                        <div className="add-outlet-time-picker-container">
                            <label htmlFor="default-end">Default end time</label>
                            <div >
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <TimePicker
                                        className='add-outlet-time-picker'
                                        value={toPickerValue(defaultEndTime)}
                                        onChange={(value) => setServiceHours((prev) => ({
                                            ...prev,
                                            orderingHours: {
                                                ...prev.orderingHours,
                                                defaultEndTime: value?.format('HH:mm') || '',
                                            },
                                        }))}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>
                    </div>

                    <label className="add-outlet-checkbox">
                        <input
                            type="checkbox"
                            checked={useCustomHours}
                            onChange={(e) => setServiceHours((prev) => ({
                                ...prev,
                                orderingHours: {
                                    ...prev.orderingHours,
                                    useCustomHours: e.target.checked,
                                },
                            }))}
                        />
                        <span>Use custom hours for specific day</span>
                    </label>
                </div>
            </div>
        </section>
    )
}

function TablesQrTab({ qrItems, tablesQr, outletId, setApiError, handleRegenerateQR }) {
    const tableList = tablesQr?.tables?.length ? tablesQr.tables : []
    const [openMenuId, setOpenMenuId] = useState(null)
    const [syncing, setSyncing] = useState(false)

    const handleDownloadQR = async (qrUrl) => {
        if (!qrUrl) {
            setApiError('No QR URL provided for download.')
            return
        }
        const response = await fetch(qrUrl);
        if (!response.ok) {
            throw new Error("Failed to download QR code");
        }
        const blob = await response.blob();
        console.log("blob", blob)
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "merchant-qr.svg";   // You decide the filename
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // if (!tableId) {
        //     setApiError('No table ID provided for download.')
        //     return
        // }
        // setApiError('') // Clear any previous error
        // try {
        //     const response = await merchantApi.outlets.downloadQR(outletId, tableId)
        //     const url = window.URL.createObjectURL(new Blob([response.data]))
        //     const link = document.createElement('a')
        //     link.href = url
        //     link.setAttribute('download', `table_${tableId}_qr.png`)
        //     document.body.appendChild(link)
        //     link.click()
        //     document.body.removeChild(link)
        // } catch (error) {
        //     console.error('Failed to download QR', error)
        //     setApiError('Failed to download QR code.')
        // }
    }

    const handleSyncPos = async () => {
        console.log('handleSyncPos', outletId)
        setSyncing(true)
        try {
            const response = await merchantApi.outlets.syncPos(outletId)
            console.log('POS sync response', response)
        } catch (error) {
            console.error('Failed to sync POS', error)
        } finally {
            setSyncing(false)
        }
    }

    const handleDownloadAllQR = async () => {
        if (tableList.length === 0) {
            setApiError('No tables available for download.')
            return
        }

        setApiError('') // Clear any previous error
        const zip = new JSZip();

    await Promise.all(

        tableList.map(async (item) => {

            const response = await fetch(item?.qrRenderUrl);

            if (!response.ok) {
                throw new Error(`Failed to download QR ${item.id}`);
            }

            const blob = await response.blob();

            let extension = "bin";

            switch (blob.type) {

                case "image/svg+xml":
                    extension = "svg";
                    break;

                case "image/png":
                    extension = "png";
                    break;

                case "image/jpeg":
                    extension = "jpg";
                    break;

            }

            zip.file(
                `outlet-${outletId}-qr-codes-${item.id}.${extension}`,
                blob
            );

        })

    );

    const zipBlob = await zip.generateAsync({
        type: "blob",
    });

    saveAs(zipBlob, "merchant-qr-codes.zip");
    }


    return (
        <section className="add-outlet-card">
            <header className="add-outlet-card__header add-outlet-card__header--actions">
                <h3>Table QR codes</h3>

                <div className="add-outlet-header-actions">
                    <button className="add-outlet-btn add-outlet-btn--ghost" type="button" onClick={() => handleDownloadAllQR()}>
                        <DownloadSimple size={16} /> Download all
                    </button>
                    <button className={`add-outlet-btn add-outlet-btn--primary`} type="button" onClick={() => handleSyncPos()}>
                        <Icon icon="ph:arrow-clockwise" className={syncing ? "syncing_icon" : ""} height={16} /> {syncing ? 'Syncing...' : 'Sync POS'}
                    </button>
                </div>
            </header>

            <div className="add-outlet-card__body">
                <div className="add-outlet-qr-grid">
                    {tableList.length > 0 ? tableList.map((qr) => (
                        <article key={qr.id} className="add-outlet-qr-card">
                            {
                                qr?.qrUrl ? <img className="add-outlet-qr-code" src={qr.qrRenderUrl} alt={`QR code for ${qr.name || qr.table_name || qr.table_code}`} />
                                    : <div className="add-outlet-qr-code">Not available</div>
                            }

                            <p className="add-outlet-qr-title">
                                {qr.name || qr.table_name || qr.table_code} <span>• {qr.pax || qr.capacity_max || 0} pax</span>
                            </p>

                            <div className="add-outlet-qr-actions">
                                <button className="add-outlet-btn add-outlet-btn--outline" type="button" style={{ width: '100%' }} onClick={() => handleDownloadQR(qr?.qrRenderUrl)}>
                                    <DownloadSimple size={14} /> Download
                                </button>

                                <AppMenu
                                    options={[
                                        {
                                            label: 'Regenerate QR',
                                            icon: 'ph:qr-code', // Optional iconify item inside the menu
                                            onClick: () => handleRegenerateQR(qr?.id),
                                        },
                                    ]}
                                />
                            </div>

                        </article>
                    )) : "No tables available"}
                </div>
            </div>
        </section>
    )
}

function BrandingTab({ branding, setBranding, apiError, setApiError }) {
    const logoUrl = branding?.logoPreviewUrl || branding?.logoImageUrl || branding?.logo_image_url
    const homepageBannerUrl = branding?.homepageBannerPreviewUrl || branding?.homepageBannerImageUrl || branding?.homepage_banner_image_url
    const storefrontUrl = branding?.storefrontPreviewUrl || branding?.storefrontImageUrl || branding?.storefront_image_url

    const hiddenInputIds = {
        logo: 'branding-logo-input',
        homepageBanner: 'branding-homepage-banner-input',
        storefront: 'branding-storefront-input',
    }

    const openFilePicker = (inputId) => {
        document.getElementById(inputId)?.click()
    }

    const handleBrandingUpload = (field) => (event) => {
        // check if file size exceeds limit (2MB for logo, 5MB for homepage banner, 3MB for storefront)

        const fileSizeLimit = field === 'logo' ? 2 * 1024 * 1024 : field === 'homepageBanner' ? 5 * 1024 * 1024 : 3 * 1024 * 1024
        const file = event.target.files?.[0]
        if (!file) return
        if (file.size > fileSizeLimit) {
            setApiError(`File size exceeds the limit for ${field === 'logo' ? 'logo' : field === 'homepageBanner' ? 'homepage banner' : 'storefront'} (${fileSizeLimit / (1024 * 1024)}MB)`)
            return
        } else {
            setApiError('')
        }

        const nextPreviewUrl = URL.createObjectURL(file)

        setBranding((prev) => {
            const previousPreviewUrl =
                field === 'logo'
                    ? prev.logoPreviewUrl
                    : field === 'homepageBanner'
                        ? prev.homepageBannerPreviewUrl
                        : prev.storefrontPreviewUrl

            if (previousPreviewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previousPreviewUrl)
            }

            return {
                ...prev,
                [`${field}File`]: file,
                [`${field}PreviewUrl`]: nextPreviewUrl,
            }
        })

        event.target.value = ''
    }

    const handleBrandingRemove = (field) => () => {
        setBranding((prev) => {
            const previousPreviewUrl =
                field === 'logo'
                    ? prev.logoPreviewUrl
                    : field === 'homepageBanner'
                        ? prev.homepageBannerPreviewUrl
                        : prev.storefrontPreviewUrl

            if (previousPreviewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previousPreviewUrl)
            }

            return {
                ...prev,
                [`${field}File`]: null,
                [`${field}PreviewUrl`]: '',
                [`${field}ImageUrl`]: '',
            }
        })
    }

    return (
        <section className="add-outlet-card">
            <header className="add-outlet-card__header">Branding</header>
            {apiError && <div className="sr-error">{apiError}</div>}
            <div className="add-outlet-card__body add-outlet-branding">
                <div className="add-outlet-field add-outlet-field--full">
                    <label>Logo</label>
                    <div className="add-outlet-logo-row">
                        <div className="add-outlet-logo-box">
                            {logoUrl ? <img src={logoUrl} alt="Outlet logo" /> : <UploadSimple size={18} />}
                        </div>
                        <input
                            id={hiddenInputIds.logo}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="add-outlet-file-input"
                            onChange={handleBrandingUpload('logo')}
                        />
                        <button className="add-outlet-btn add-outlet-btn--outline" type="button" onClick={() => openFilePicker(hiddenInputIds.logo)}>{logoUrl ? 'Replace' : 'Upload'}</button>
                        <button className="add-outlet-btn add-outlet-btn--danger" type="button" onClick={handleBrandingRemove('logo')}>Remove</button>
                    </div>
                    <p>JPG, PNG or WEBP • max 2MB • 600x600 recommended</p>
                </div>

                <div className="add-outlet-field add-outlet-field--full">
                    <label>Homepage banner</label>
                    <input
                        id={hiddenInputIds.homepageBanner}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="add-outlet-file-input"
                        onChange={handleBrandingUpload('homepageBanner')}
                    />
                    <button className="add-outlet-dropzone" type="button" onClick={() => openFilePicker(hiddenInputIds.homepageBanner)}>
                        {homepageBannerUrl ? <img src={homepageBannerUrl} alt="Homepage banner" /> : <ImageSquare size={22} />}
                        {!homepageBannerUrl && <strong>Drop image or click to upload</strong>}
                        {!homepageBannerUrl && <span>JPG, PNG or WEBP • max 5MB • 1600x1600 recommended</span>}
                    </button>
                    {homepageBannerUrl && (
                        <button className="add-outlet-btn add-outlet-btn--danger add-outlet-btn--sm" type="button" onClick={handleBrandingRemove('homepageBanner')}>
                            Remove banner
                        </button>
                    )}
                </div>

                <div className="add-outlet-field add-outlet-field--full">
                    <label>
                        Storefront image <span>(optional)</span>
                    </label>
                    <input
                        id={hiddenInputIds.storefront}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="add-outlet-file-input"
                        onChange={handleBrandingUpload('storefront')}
                    />
                    <button className="add-outlet-dropzone" type="button" onClick={() => openFilePicker(hiddenInputIds.storefront)}>
                        {storefrontUrl ? <img src={storefrontUrl} alt="Storefront" /> : <ImageSquare size={22} />}
                        {!storefrontUrl && <strong>Drop image or click to upload</strong>}
                        {!storefrontUrl && <span>JPG, PNG or WEBP • max 3MB • 1300x480 recommended</span>}
                    </button>
                    {storefrontUrl && (
                        <button className="add-outlet-btn add-outlet-btn--danger add-outlet-btn--sm" type="button" onClick={handleBrandingRemove('storefront')}>
                            Remove storefront image
                        </button>
                    )}
                </div>
            </div>
        </section>
    )
}

const KIOSK_STATUS = {
    0: 'Inactive',
    1: 'Active',
}

const getKioskStatusLabel = (status) => KIOSK_STATUS[Number(status)] || 'Inactive'

const formatDateTime = (value) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString()
}

const normalizeKioskList = (response) => {
    const source = response?.data || response || []
    const items = Array.isArray(source) ? source : (source.data || source.items || [])
    return Array.isArray(items) ? items : []
}

const getMerchantToken = () => {
    const keys = ['merchant_token', 'merchantToken', 'auth_token', 'authToken', 'token', 'access_token']
    for (const key of keys) {
        const value = localStorage.getItem(key)
        if (value) return value
    }
    return ''
}

const kioskRequest = async (method, path, payload) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_MERCHANT_API_URL || ''
    const token = getMerchantToken()
    const response = await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: payload ? JSON.stringify(payload) : undefined,
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
        const error = new Error(data?.message || 'Request failed.')
        error.errors = data?.errors || {}
        throw error
    }
    return data
}

const kioskApi = {
    async list(outletId, page = 1) {
        if (merchantApi.kiosks?.list) {
            return merchantApi.kiosks.list({ "filter[outlet_id]": outletId, "page": page })
        }

        return kioskRequest('GET', `/kiosks?filter[outlet_id]=${encodeURIComponent(outletId)}&page=${encodeURIComponent(page)}&size=20`)
    },
    async create(payload) {
        if (merchantApi.kiosks?.create) return merchantApi.kiosks.create(payload)
        return kioskRequest('POST', '/kiosks', payload)
    },
    async update(id, payload) {
        if (merchantApi.kiosks?.update) return merchantApi.kiosks.update(id, payload)
        return kioskRequest('PUT', `/kiosks/${id}`, payload)
    },
    async remove(id) {
        if (merchantApi.kiosks?.delete) return merchantApi.kiosks.delete(id)
        if (merchantApi.kiosks?.destroy) return merchantApi.kiosks.destroy(id)
        return kioskRequest('DELETE', `/kiosks/${id}`)
    },
    async forceLogout(id) {
        if (merchantApi.kiosks?.forceLogout) return merchantApi.kiosks.forceLogout(id)
        return kioskRequest('POST', `/kiosks/${id}/force-logout`)
    },
}

function KiosksTab({ outletId }) {
    const [kiosks, setKiosks] = useState([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')
    const [editingKiosk, setEditingKiosk] = useState(null)
    const [formOpen, setFormOpen] = useState(false)
    const [form, setForm] = useState({ name: '', code: '', pin: '', status: 1 })
    const [formErrors, setFormErrors] = useState({})
    const [currentPage, setCurrentPage] = useState(1)
    const [pagination, setPagination] = useState({})

    const canManage = Boolean(outletId)

    const loadKiosks = async () => {
        if (!outletId) return
        setLoading(true)
        setError('')
        try {
            const response = await kioskApi.list(outletId, currentPage)
            setKiosks(normalizeKioskList(response))
            setPagination({
                total: response?.data?.total || 0,
                from: response?.data?.from || 0,
                to: response?.data?.to || 0,
                last_page: response?.data?.last_page || 0,
            })
        } catch (err) {
            setError(err?.message || 'Unable to load kiosks.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadKiosks()
    }, [outletId, currentPage])

    const openCreateForm = () => {
        setEditingKiosk(null)
        setForm({ name: '', code: '', pin: '', status: 1, cloudecr_pos_id: '' })
        setFormErrors({})
        setNotice('')
        setError('')
        setFormOpen(true)
    }

    const openEditForm = (kiosk) => {
        setEditingKiosk(kiosk)
        setForm({
            name: kiosk.name || '',
            code: kiosk.code || '',
            pin: '',
            status: Number(kiosk.status ?? 1),
            // cloudecr_pos_id: kiosk.cloudecr_pos_id || '',
        })
        setFormErrors({})
        setNotice('')
        setError('')
        setFormOpen(true)
    }

    const updateForm = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }))
        setFormErrors((prev) => ({ ...prev, [key]: '' }))
    }

    const validateForm = () => {
        const nextErrors = {}
        if (!form.name?.trim()) nextErrors.name = 'Kiosk name is required.'
        if (!form.code?.trim()) nextErrors.code = 'Kiosk code is required.'
        if (!editingKiosk && !form.pin?.trim()) nextErrors.pin = 'PIN is required.'
        if (form.pin && !/^\d{4}$/.test(form.pin)) nextErrors.pin = 'PIN must be 4 digits.'
        setFormErrors(nextErrors)
        return !Object.keys(nextErrors).length
    }

    const saveKiosk = async () => {
        if (!canManage || saving || !validateForm()) return

        setSaving(true)
        setError('')
        setNotice('')

        try {
            const payload = {
                outlet_id: Number(outletId),
                name: form.name.trim(),
                code: form.code.trim().toUpperCase(),
                status: Number(form.status),
                // cloudecr_pos_id: form.cloudecr_pos_id,
                ...(form.pin ? { pin: form.pin } : {}),
            }

            if (editingKiosk?.id) {
                await kioskApi.update(editingKiosk.id, payload)
                setNotice('Kiosk updated successfully.')
            } else {
                await kioskApi.create(payload)
                setNotice('Kiosk created successfully.')
            }

            setFormOpen(false)
            await loadKiosks()
        } catch (err) {
            setFormErrors(err?.errors || {})
            setError(err?.message || 'Unable to save kiosk.')
        } finally {
            setSaving(false)
        }
    }

    const toggleStatus = async (kiosk) => {
        if (saving) return
        setSaving(true)
        setError('')
        setNotice('')
        try {
            await kioskApi.update(kiosk.id, {
                outlet_id: Number(outletId),
                name: kiosk.name,
                code: kiosk.code,
                status: Number(kiosk.status) === 1 ? 0 : 1,
                // cloudecr_pos_id: kiosk.cloudecr_pos_id || '',
            })
            setNotice(Number(kiosk.status) === 1 ? 'Kiosk disabled.' : 'Kiosk enabled.')
            await loadKiosks()
        } catch (err) {
            setError(err?.message || 'Unable to update kiosk status.')
        } finally {
            setSaving(false)
        }
    }

    const forceLogout = async (kiosk) => {
        if (saving) return
        setSaving(true)
        setError('')
        setNotice('')
        try {
            await kioskApi.forceLogout(kiosk.id)
            setNotice('Kiosk session logged out successfully.')
            await loadKiosks()
        } catch (err) {
            setError(err?.message || 'Unable to force logout kiosk session.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <section className="add-outlet-card add-outlet-kiosks">
            <header className="add-outlet-card__header add-outlet-card__header--actions">
                <div>
                    <h3>Kiosk management</h3>
                    <p>Manage kiosk login code, 4-digit PIN, and active kiosk sessions for this outlet.</p>
                </div>
                <div className="add-outlet-header-actions">
                    <button className="add-outlet-btn add-outlet-btn--outline" type="button" onClick={loadKiosks} disabled={!canManage || loading}>
                        <ArrowsClockwise size={16} /> Refresh
                    </button>
                    <button className="add-outlet-btn add-outlet-btn--primary" type="button" onClick={openCreateForm} disabled={!canManage}>
                        + Add Kiosk
                    </button>
                </div>
            </header>

            <div className="add-outlet-card__body">
                {!canManage && <div className="add-outlet-empty-state">Save the outlet first before adding kiosks.</div>}
                {error && <div className="sr-error">{error}</div>}
                {notice && <div className="sr-success">{notice}</div>}

                {formOpen && (
                    <div className="add-outlet-kiosk-form">
                        <div className="add-outlet-form-grid">
                            <FormGroup label="Kiosk name" error={formErrors.name}>
                                <input
                                    type="text"
                                    placeholder="Front counter kiosk"
                                    value={form.name}
                                    onChange={(e) => updateForm('name', e.target.value)}
                                />
                            </FormGroup>
                            <FormGroup label="Kiosk code" error={formErrors.code}>
                                <input
                                    type="text"
                                    placeholder="KIOSK1"
                                    value={form.code}
                                    onChange={(e) => updateForm('code', e.target.value.toUpperCase())}
                                />
                            </FormGroup>
                            <FormGroup label={editingKiosk ? 'New 4-digit PIN' : '4-digit PIN'} optional={Boolean(editingKiosk)} error={formErrors.pin}>
                                <input
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={4}
                                    placeholder={editingKiosk ? 'Leave empty to keep current PIN' : '1234'}
                                    value={form.pin}
                                    onChange={(e) => {
                                        if (/^\d{0,4}$/.test(e.target.value)) updateForm('pin', e.target.value)
                                    }}
                                />
                            </FormGroup>
                            <FormGroup label="Status" error={formErrors.status}>
                                <select value={form.status} onChange={(e) => updateForm('status', Number(e.target.value))}>
                                    <option value={1}>Active</option>
                                    <option value={0}>Inactive</option>
                                </select>
                            </FormGroup>
                            {/* <FormGroup label="CloudECR POS ID" error={formErrors.cloudecr_pos_id}>
                                <input
                                    type="text"
                                    placeholder="POS1234"
                                    value={form.cloudecr_pos_id}
                                    onChange={(e) => updateForm('cloudecr_pos_id', e.target.value.toUpperCase())}
                                />
                            </FormGroup> */}
                        </div>
                        <div className="add-outlet-kiosk-form__actions">
                            <button className="add-outlet-btn add-outlet-btn--outline" type="button" onClick={() => setFormOpen(false)} disabled={saving}>
                                Cancel
                            </button>
                            <button className="add-outlet-btn add-outlet-btn--primary" type="button" onClick={saveKiosk} disabled={saving}>
                                {saving ? 'Saving...' : editingKiosk ? 'Update Kiosk' : 'Create Kiosk'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="add-outlet-kiosk-table-wrap">
                    <table className="add-outlet-kiosk-table">
                        <thead>
                            <tr>
                                <th>Kiosk</th>
                                <th>Code</th>
                                <th>Status</th>
                                <th>Session</th>
                                <th>Last login</th>
                                <th>Last seen</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7">Loading kiosks...</td></tr>
                            ) : kiosks.length ? kiosks.map((kiosk) => {
                                const hasActiveSession = Boolean(kiosk.current_session_id || kiosk.current_session)
                                return (
                                    <tr key={kiosk.id}>
                                        <td>
                                            <strong>{kiosk.name}</strong>
                                            <span>{kiosk.outlet?.name || ''}</span>
                                        </td>
                                        <td><code>{kiosk.code}</code></td>
                                        <td>
                                            <span className={`add-outlet-kiosk-status add-outlet-kiosk-status--${Number(kiosk.status) === 1 ? 'active' : 'inactive'}`}>
                                                {getKioskStatusLabel(kiosk.status)}
                                            </span>
                                        </td>
                                        <td>{hasActiveSession ? <span className="add-outlet-kiosk-session">Logged in</span> : '-'}</td>
                                        <td>{formatDateTime(kiosk.last_login_at)}</td>
                                        <td>{formatDateTime(kiosk.last_seen_at)}</td>
                                        <td>
                                            <div className="add-outlet-kiosk-actions">
                                                <button type="button" onClick={() => openEditForm(kiosk)}>Edit</button>
                                                <button type="button" onClick={() => toggleStatus(kiosk)} disabled={saving}>
                                                    {Number(kiosk.status) === 1 ? 'Disable' : 'Enable'}
                                                </button>
                                                <button type="button" onClick={() => forceLogout(kiosk)} disabled={saving || !hasActiveSession}>Force logout</button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            }) : (
                                <tr><td colSpan="7">No kiosk added yet.</td></tr>
                            )}
                        </tbody>
                    </table>

                </div>
                <AppPagination
                    currentPage={currentPage}
                    pagination={pagination}
                    onPageChange={(page) => setCurrentPage(page)}
                />
            </div>
        </section>
    )
}

export default function AddOutletPage() {
    const { type, id } = useParams()
    const [activeTab, setActiveTab] = useState('outlet-info')
    const [outletInfo, setOutletInfo] = useState({})
    const [serviceHours, setServiceHours] = useState({})
    const [tablesQr, setTablesQr] = useState({})
    const [branding, setBranding] = useState({})
    const [outletSource, setOutletSource] = useState(null)
    const [notice, setNotice] = useState()
    const [submitting, setSubmitting] = useState(false)
    const [apiError, setApiError] = useState('')
    const [errors, setErrors] = useState({})
    const [userInfo, setUserInfo] = useState()
    const navigate = useNavigate()

    useEffect(() => {
        loadUserInfo()
    }, [])

    useEffect(() => {
        if (type === 'edit' && id) {
            // Fetch existing outlet data and populate the form
            loadOutletData()
        }
    }, [type, id])

    const loadUserInfo = async () => {
        try {
            const response = await merchantApi.auth.me()
            setUserInfo(response.data)
        } catch (e) {
            setApiError(e?.message || 'Failed to load user info.')
        }
    }

    const hydrateOutletState = (outletData) => {
        const outletInfoData = outletData.outlet_info || outletData
        const serviceHoursDetail = outletData.service_hours_detail || {}
        const businessHours = serviceHoursDetail.business_hours || outletData.service_hours || []
        const orderingHours = serviceHoursDetail.ordering_hours || {}
        const tablesQrData = outletData.tables_qr || {}
        const brandingData = outletData.branding || {}
        setOutletSource(outletData)
        setOutletInfo({
            name: outletInfoData.display_name || outletInfoData.name || '',
            address: outletInfoData.address || outletData.address || '',
            mapsUrl: outletInfoData.google_maps_url || outletData.google_maps_url || '',
            longitude: outletInfoData.longitude || outletData.longitude || '',
            latitude: outletInfoData.latitude || outletData.latitude || '',
            phoneNumber: parsePhoneNumberFromString((outletInfoData.phone || outletData.phone || '')?.toString())?.nationalNumber || '',
            countryCode: parsePhoneNumberFromString((outletInfoData.phone || outletData.phone || '')?.toString())?.countryCallingCode || '60',
            email: outletInfoData.email || outletData.email || '',
            cartInactivityTimeout: outletInfoData.cart_inactivity_timeout_minutes || outletData.cart_inactivity_timeout_minutes || 15,
        })

        setServiceHours({
            businessHours: businessHours.map((hour) => ({
                day: hour.day,
                time: hour.display || (hour.is_closed ? 'Closed' : `${toDisplayTime(hour.open_time)} - ${toDisplayTime(hour.close_time)}`),
                isClosed: Boolean(hour.is_closed),
            })),
            orderingHours: {
                defaultStartTime: toDisplayTime(orderingHours.default_start_time) || '9:30 am',
                defaultEndTime: toDisplayTime(orderingHours.default_end_time) || '4:00 pm',
                useCustomHours: Boolean(orderingHours.use_custom_hours),
                hours: Array.isArray(orderingHours.hours) ? orderingHours.hours : [],
            },
        })

        setTablesQr({
            tableCount: tablesQrData.table_count || outletData.table_count || 0,
            maxPax: tablesQrData.max_pax || outletData.max_pax || 0,
            tables: Array.isArray(tablesQrData.tables) ? tablesQrData.tables.map((table) => ({
                id: table.id,
                name: table.name || table.table_name || table.table_code,
                pax: table.pax || table.capacity_max || 0,
                qrUrl: table.qr_url,
                qrPayload: table.qr_payload,
                qrRenderUrl: table.qr_render_url,
            })) : [],
        })

        setBranding({
            logoImageUrl: outletData.logo_image_url || brandingData.logo_image_url || '',
            homepageBannerImageUrl: outletData.homepage_banner_image_url || brandingData.homepage_banner_image_url || '',
            storefrontImageUrl: outletData.storefront_image_url || brandingData.storefront_image_url || '',
        })
    }

    const loadOutletData = async () => {
        try {
            const response = await merchantApi.outlets.show(id)
            hydrateOutletState(response?.data || {})
        } catch (error) {
            console.error('Error fetching outlet data:', error)
            setApiError('Failed to load outlet data.')
        }
    }

    const handleOutletInfoChange = (field, value) => {
        setOutletInfo((prev) => ({ ...prev, [field]: value }))
        setErrors((prev) => ({ ...prev, [field]: '' }))
        setApiError('')
    }

    const validate = (externalErrors = {}) => {
        const e = { ...externalErrors }

        if (!outletInfo.name?.trim()) e.name = 'Outlet display name is required'
        if (!outletInfo.address?.trim()) e.address = 'Address is required'
        if (!outletInfo.mapsUrl?.trim()) e.mapsUrl = 'Google maps URL is required'
        else if (!/^https?:\/\/.+/.test(outletInfo.mapsUrl.trim())) e.mapsUrl = 'Enter a valid URL starting with http:// or https://'
        if (outletInfo.longitude && !/^-?\d+(\.\d+)?$/.test(outletInfo.longitude.trim())) e.longitude = 'Must be numeric'
        if (outletInfo.latitude && !/^-?\d+(\.\d+)?$/.test(outletInfo.latitude.trim())) e.latitude = 'Must be numeric'
        if (!outletInfo.phoneNumber?.trim()) e.phoneNumber = 'Phone number is required'
        else if (!/^\d+$/.test(outletInfo.phoneNumber.trim())) e.phoneNumber = 'Must be numeric'
        if (outletInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(outletInfo.email.trim())) e.email = 'Enter a valid email address'

        setErrors(e)
        return !Object.keys(e).length
    }

    const buildOutletInfoPayload = () => ({
        tab: 'outlet_info',
        name: outletInfo.name?.trim(),
        address: outletInfo.address?.trim(),
        google_maps_url: outletInfo.mapsUrl?.trim(),
        longitude: outletInfo.longitude?.trim() || null,
        latitude: outletInfo.latitude?.trim() || null,
        phone: `+${outletInfo.countryCode || '60'}${outletInfo.phoneNumber?.trim()}`,
        email: outletInfo.email?.trim() || null,
        status: 1,
        cart_inactivity_timeout_minutes: Number(outletInfo.cartInactivityTimeout) || 15,
    })

    const buildServiceHoursPayload = () => {
        const sourceBusinessHours = outletSource?.service_hours_detail?.business_hours || outletSource?.service_hours || []
        const sourceOrderingHours = outletSource?.service_hours_detail?.ordering_hours || {}

        const businessHours = sourceBusinessHours.length
            ? sourceBusinessHours.map((hour) => ({
                day_of_week: hour.day_of_week,
                is_closed: Boolean(hour.is_closed),
                ...(hour.is_closed
                    ? {}
                    : {
                        open_time: toApiTime(hour.open_time),
                        close_time: toApiTime(hour.close_time),
                    }),
            }))
            : []

        return {
            business_hours: businessHours,
            ordering_hours: {
                default_start_time: toApiTime(serviceHours?.orderingHours?.defaultStartTime) || toApiTime(sourceOrderingHours.default_start_time) || '09:30',
                default_end_time: toApiTime(serviceHours?.orderingHours?.defaultEndTime) || toApiTime(sourceOrderingHours.default_end_time) || '16:00',
                use_custom_hours: Boolean(serviceHours?.orderingHours?.useCustomHours),
            },
        }
    }

    const buildTablesQrPayload = () => {
        const sourceTables = outletSource?.tables_qr?.tables || []
        const tables = sourceTables.length
            ? sourceTables.map((table) => ({
                table_code: table.table_code || table.name || table.table_name,
                table_name: table.table_name || table.table_code || table.name,
                capacity_max: table.capacity_max || table.pax || 0,
            }))
            : tablesQr?.tables?.map((table, index) => ({
                table_code: table.table_code || table.name || `Table ${index + 1}`,
                table_name: table.table_name || table.name || `Table ${index + 1}`,
                capacity_max: table.capacity_max || table.pax || 0,
            })) || []

        return { tables }
    }

    const buildBrandingPayload = () => {
        const formData = new FormData()
        let hasFile = false
        formData.append('tab', 'branding')
        if (branding.logoFile) {
            formData.append('logo_image', branding.logoFile)
            hasFile = true
        }

        if (branding.homepageBannerFile) {
            formData.append('homepage_banner_image', branding.homepageBannerFile)
            hasFile = true
        }

        if (branding.storefrontFile) {
            formData.append('storefront_image', branding.storefrontFile)
            hasFile = true
        }

        return hasFile ? formData : null
    }

    const handleSubmit = async () => {
        // if (!validate()) {
        //     setActiveTab('outlet-info')
        //     return
        // }

        setSubmitting(true)
        setApiError('')
        setNotice(null)

        try {
            let resolvedOutletId = id
            let response = null

            const outletInfoPayload = buildOutletInfoPayload()
            response = resolvedOutletId
                ? await merchantApi.outlets.update(resolvedOutletId, outletInfoPayload)
                : await merchantApi.outlets.create(outletInfoPayload)

            const outletResponseData = response?.data || {}
            resolvedOutletId = outletResponseData.id || resolvedOutletId

            if (type === 'edit') {
                if (resolvedOutletId && serviceHours) {
                    await merchantApi.outlets.update(resolvedOutletId, { tab: 'service_hours', ...buildServiceHoursPayload() })
                }

                // if (resolvedOutletId && tablesQr) {
                //     await merchantApi.outlets.update(resolvedOutletId, { tab: 'tables_qr', ...buildTablesQrPayload() })
                // }

                if (resolvedOutletId && branding) {
                    const brandingPayload = buildBrandingPayload()
                    if (brandingPayload) {
                        await merchantApi.outlets.updateBranding(resolvedOutletId, brandingPayload, true)
                    }
                }
            }

            if (response?.data) {
                // hydrateOutletState(response.data)
                loadOutletData() // Refresh the outlet data after saving
            }

            setNotice('Outlet saved successfully.')
        } catch (err) {
            validate(err?.data?.errors) // Re-validate to show errors if any
            setApiError(err?.message || 'Unable to create outlet.')
        } finally {
            setSubmitting(false)
        }
    }

    const handleRegenerateQR = async (tableId) => {
        try {
            const response = await merchantApi.outlets.regenerateQR(id || outletSource?.id, tableId)
            console.log('Regenerate QR response', response)
            loadOutletData() // Refresh the outlet data to get the updated QR code
        } catch (error) {
            console.error('Failed to regenerate QR', error)
        }
    }

    return (
        <div className="add-outlet-page">
            <div className="add-outlet-page__header-row">
                <Header
                    showBack
                    onBack={() => navigate('/store-setup')}
                    backLabel="Back"
                    items={[
                        { label: userInfo?.merchant?.business_name || 'Merchant', to: '/store-setup' },
                        { label: type === 'edit' ? 'Edit outlet' : 'Add outlet', current: true },
                    ]}
                />

                <div className="add-outlet-page__header-actions">
                    <button className="add-outlet-btn add-outlet-btn--outline" type="button" onClick={() => navigate('/store-setup')}>
                        Cancel
                    </button>
                    <button className="add-outlet-btn add-outlet-btn--primary" type="button" disabled={submitting} onClick={() => handleSubmit()}>
                        {submitting ? 'Saving...' : 'Save changes'}
                    </button>
                </div>
            </div>

            {apiError && <div className="sr-error">{apiError}</div>}
            {notice && <div className="sr-success">{notice}</div>}

            <div className="add-outlet-tabs" role="tablist" aria-label="Add outlet sections">
                {FORM_TABS.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={`add-outlet-tab ${activeTab === tab.key ? 'add-outlet-tab--active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'outlet-info' && <OutletInfoTab outletInfo={outletInfo} setOutletInfo={handleOutletInfoChange} errors={errors} />}
            {activeTab === 'service-hours' && <ServiceHoursTab serviceHours={serviceHours} setServiceHours={setServiceHours} />}
            {activeTab === 'tables-qr' && <TablesQrTab tablesQr={tablesQr} setTablesQr={setTablesQr} outletId={id || outletSource?.id} setApiError={setApiError} handleRegenerateQR={handleRegenerateQR} />}
            {activeTab === 'branding' && <BrandingTab branding={branding} setBranding={setBranding} apiError={apiError} setApiError={setApiError} />}
            {activeTab === 'kiosks' && <KiosksTab outletId={id || outletSource?.id} />}
        </div>
    )
}
